import React, { useEffect, useRef, useState } from 'react';
import { setLoadingHandler } from '../services/api';

// A thin indeterminate progress bar pinned to the very top of the app. It is
// driven by the API client's in-flight ACTION counter (see setLoadingHandler in
// services/api.ts) — so it appears whenever a user action (reply, advance,
// resolve, complete, edit, assign…) is in flight, and stays out of the way of
// the 4s feed poll / SignalR reloads (those are GETs and never counted).
//
// Three guards keep it pleasant and crash-proof:
//   • SHOW_DELAY  — wait a beat before showing, so an instant action never flashes.
//   • MIN_VISIBLE — once shown, stay up briefly, so quick actions don't flicker.
//   • FAILSAFE    — force-hide after a hard cap, so a leaked counter can NEVER
//                   leave the bar stuck on (a stuck bar reads as a frozen app).
const SHOW_DELAY = 120;
const MIN_VISIBLE = 350;
const FAILSAFE = 20000;

const GlobalLoadingBar: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const visibleRef = useRef(false);
  const showTimer = useRef<number | undefined>(undefined);
  const hideTimer = useRef<number | undefined>(undefined);
  const failsafe = useRef<number | undefined>(undefined);
  const shownAt = useRef(0);

  const setShown = (v: boolean) => { visibleRef.current = v; setVisible(v); };

  useEffect(() => {
    setLoadingHandler((active) => {
      if (active) {
        window.clearTimeout(hideTimer.current);
        if (!visibleRef.current && showTimer.current === undefined) {
          showTimer.current = window.setTimeout(() => {
            showTimer.current = undefined;
            shownAt.current = Date.now();
            setShown(true);
          }, SHOW_DELAY);
        }
        window.clearTimeout(failsafe.current);
        failsafe.current = window.setTimeout(() => setShown(false), FAILSAFE);
      } else {
        if (showTimer.current !== undefined) {
          window.clearTimeout(showTimer.current);
          showTimer.current = undefined;
        }
        window.clearTimeout(failsafe.current);
        if (visibleRef.current) {
          const wait = Math.max(0, MIN_VISIBLE - (Date.now() - shownAt.current));
          hideTimer.current = window.setTimeout(() => setShown(false), wait);
        }
      }
    });
    return () => {
      setLoadingHandler(null);
      window.clearTimeout(showTimer.current);
      window.clearTimeout(hideTimer.current);
      window.clearTimeout(failsafe.current);
    };
  }, []);

  return (
    <>
      <style>{`@keyframes slx-indeterminate {
        0%   { left: -40%; width: 40%; }
        50%  { left: 25%;  width: 55%; }
        100% { left: 100%; width: 40%; }
      }`}</style>
      <div
        aria-hidden={!visible}
        role="progressbar"
        className="fixed top-0 left-0 right-0 z-[60] h-1 pointer-events-none transition-opacity duration-200"
        style={{ opacity: visible ? 1 : 0 }}
      >
        <div className="relative h-full w-full bg-brand-100 overflow-hidden">
          {visible && (
            <div
              className="absolute top-0 h-full bg-brand-700 rounded-full"
              style={{ animation: 'slx-indeterminate 1.1s ease-in-out infinite' }}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default GlobalLoadingBar;
